/**
 * Per-topic rollup: mastery distribution across the class, average score and the
 * dominant shared misconception. Click a topic to open item analysis.
 */

import { ChevronRight, AlertTriangle } from 'lucide-react';
import type { ClassRoster } from './mockData';
import { topicStats, misconceptionLabel } from './mockData';
import { Panel, StackedMasteryBar } from './charts';

export function TopicBreakdown({ roster, onSelectTopic }: { roster: ClassRoster; onSelectTopic: (topicId: string) => void }) {
  return (
    <Panel title="Topics" sub="How the class is doing across the syllabus so far">
      <div className="flex flex-col gap-3">
        {roster.topics.map((t) => {
          const s = topicStats(roster, t.id);
          const attempted = s.mastered + s.developing + s.emerging;
          return (
            <button key={t.id} onClick={() => onSelectTopic(t.id)}
              className="group rounded-xl border border-black/[0.07] bg-black/[0.015] p-4 text-left transition-colors hover:border-black/15 hover:bg-black/[0.03] dark:border-white/[0.08] dark:bg-white/[0.02] dark:hover:bg-white/[0.05]">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="truncate font-serif text-[15px] font-semibold tracking-tight">{t.title}</h4>
                  <div className="mt-0.5 text-[11.5px] text-[#8A8593]">
                    {attempted} of {roster.learners.length} attempted · {t.totalQuestions} questions
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <div className="text-right">
                    <div className="font-serif text-[20px] font-medium leading-none tabular-nums" style={{ color: s.avgScorePercent >= 70 ? '#1F6B5F' : s.avgScorePercent >= 55 ? '#9A7320' : '#B23B4E' }}>{s.avgScorePercent}%</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8A8593]">avg score</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#8A8593] transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>

              <div className="mt-3">
                <StackedMasteryBar mastered={s.mastered} developing={s.developing} emerging={s.emerging} notAttempted={s.notAttempted} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-[#56505F] dark:text-[#B2B0C0]">
                <span><b className="tabular-nums">{s.mastered}</b> mastered</span>
                <span><b className="tabular-nums">{s.developing}</b> developing</span>
                <span><b className="tabular-nums">{s.emerging}</b> emerging</span>
                {s.notAttempted > 0 && <span><b className="tabular-nums">{s.notAttempted}</b> not started</span>}
              </div>

              {s.dominantMisconception && (
                <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-[#CB5B6B]/[0.08] px-3 py-2 text-[12px] leading-[1.45] text-[#B23B4E]">
                  <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                  <span>
                    <b>{s.dominantMisconception.holders} learners</b> share a misconception here: {misconceptionLabel(s.dominantMisconception.tag).replace(/\.$/, '').toLowerCase()}.
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </Panel>
  );
}
